import { AxiosResponse } from 'axios';
import { Observable, Subject } from 'rxjs';
import { Action } from '../models/action.model';
import { ActionFilters, SubscriberEvent, TableFilters } from '../shared';
import { buildUrl } from '../utils';
import { httpClient } from './http.client';
import { EntityAction, RestService, URL } from './rest.service';
import { PaginationService } from '.';
import { Pagination } from './pagination.service';
import { Page } from '../models';
import { UploadFile } from 'antd/lib/upload/interface';

export interface ActionSubscriberEvent extends SubscriberEvent<Action> {
  files?: UploadFile[];
}

class ActionService implements RestService<Action> {
  private actionChangedSubject = new Subject<ActionSubscriberEvent>();

  get(params?: any): Promise<AxiosResponse<Action>> {
    throw new Error('Method not implemented.');
  }

  getAll(): Promise<AxiosResponse<Action[]>> {
    throw new Error('Method not implemented.');
  }

  getById(resourceId: string | number, params?: any): Promise<AxiosResponse<Action>> {
    throw new Error('Method not implemented.');
  }

  post(body?: any, params?: any): Promise<AxiosResponse<Action>> {
    throw new Error('Method not implemented.');
  }

  put(body?: any, params?: any): Promise<AxiosResponse<Action>> {
    throw new Error('Method not implemented.');
  }

  create(resource: Action): Promise<AxiosResponse<Action>> {
    return httpClient.post<Action>(URL.ACTION, resource);
  }

  createNew(action: Action, files: UploadFile[]) {
    const formData = new FormData();
    formData.append('action', new Blob([JSON.stringify(action)], { type: 'application/json' }));
    if (files.length) {
      files.forEach(({ originFileObj, name }) => {
        if (originFileObj) {
          const blob = new Blob([originFileObj], { type: originFileObj.type });
          formData.append('files', blob, name);
        }
      });
    }
    return httpClient.post<Action>(URL.ACTION, formData);
  }

  update(resource: Action): Promise<AxiosResponse<Action>> {
    return httpClient.put<Action>(URL.ACTION, resource);
  }

  assign(resource: Action): Promise<AxiosResponse<Action>> {
    return httpClient.put<Action>(buildUrl(URL.ACTION, ['assign']), resource);
  }

  changeStatus(resource: Action, files: UploadFile[] = []): Promise<AxiosResponse<Action>> {
    if (!files.length) {
      return httpClient.put<Action>(buildUrl(URL.ACTION, ['changeStatus']), resource);
    }
    const formData = new FormData();
    formData.append('action', new Blob([JSON.stringify(resource)], { type: 'application/json' }));
    files.forEach(({ originFileObj, name }) => {
      if (originFileObj) {
        const blob = new Blob([originFileObj], { type: originFileObj.type });
        formData.append('files', blob, name);
      }
    });
    return httpClient.put<Action>(buildUrl(URL.ACTION, ['changeStatus']), formData);
  }

  delete(resourceId: string | number): Promise<AxiosResponse<Action>> {
    return httpClient.delete<Action>(buildUrl(URL.ACTION, [resourceId]));
  }

  getCompanyActions(pagination: Pagination): Promise<AxiosResponse<Page<Action>>> {
    let params = {};
    params = {
      pageNumber: pagination.pageNumber ? pagination.pageNumber - 1 : 0,
      pageSize: pagination.pageSize,
    };
    return httpClient.get<Page<Action>>(buildUrl(URL.ACTION, ['allc']), { params: params });
  }

  getByIncidentId(incidentId: string): Promise<AxiosResponse<Action[]>> {
    return httpClient.get<Action[]>(buildUrl(URL.ACTION, ['byIncident', incidentId]));
  }

  getAllForCompanyAndDivision(): Promise<AxiosResponse<Action[]>> {
    return httpClient.get<Action[]>(buildUrl(URL.ACTION, ['allForCompanyAndDivision']));
  }

  getAllPaginatedAndFiltered(filters: ActionFilters): Promise<AxiosResponse<Page<Action>>> {
    const createdFilters = PaginationService.createFilters(filters);
    if (filters.dateRange) createdFilters['dateRange'] = { stringValues: filters.dateRange };

    return httpClient.post<Page<Action>>(buildUrl(URL.ACTION, ['paginated']), createdFilters, {
      params: PaginationService.createPagination(filters),
    });
  }

  getDirectCsv(filters: TableFilters): Promise<AxiosResponse<string>> {
    const createdFilters = PaginationService.createFilters(filters);
    return httpClient.post<string>(buildUrl(URL.ACTION, ['export']), createdFilters, {
      headers: { 'Content-Type': 'application/json' },
    });
  }

  // SUBSCRIPTIONS

  actionChanged(event: EntityAction, action: Action, files?: UploadFile[]) {
    this.actionChangedSubject.next({ event: event, entity: action, files: files });
  }

  getActionChangedListener(): Observable<SubscriberEvent<Action>> {
    return this.actionChangedSubject.asObservable();
  }
}

export default new ActionService();
